/* eslint-disable no-empty */
/* eslint-disable eqeqeq */
import { InitSubApp } from '../SubApp';
import { Observable, BehaviorSubject } from 'rxjs';
import {
  AmplifyApp,
  NavigationLink,
  Component,
  SubApp,
  UrlHistory,
  CoreNavigationLink,
  AmplifyWidget,
  CSSInfo,
  Version,
} from '@amplify/amplify-sdk';
import config from '../Config';
import axios, { AxiosInstance } from 'axios';
import packageJson from '../../package.json';
import AppDetails from '../components/AppDetails';
import { JwtToken } from './Types';

const appBaseUrls = new Map<string, string>();
const canonicalSlugs = new Map<string, string>();

function normalizeSlug(slug: string): string {
  return slug.replace(/['"]/g, '').trim().toLowerCase();
}

export function getAppBaseUrl(slug: string): string {
  const key = normalizeSlug(slug);
  const fromMap = appBaseUrls.get(key);
  if (!fromMap) {
    console.warn(`[SubAppLoader] No baseUrl registered for slug "${slug}" (key="${key}"); falling back to REACT_APP_CDN_HOST.`);
  }
  return (fromMap || process.env.REACT_APP_CDN_HOST || '').replace(/\/+$/, '');
}

/**
 * Returns the canonical (API-supplied) slug casing for use in URL paths.
 * Sub-apps may report their slug in a different case at runtime; this keeps
 * generated asset paths aligned with what the origin actually serves.
 * Falls back to the cleaned input slug when the canonical value is unknown.
 */
export function getAppSlug(slug: string): string {
  const cleaned = slug.replace(/['"]/g, '').trim();
  return canonicalSlugs.get(normalizeSlug(cleaned)) || cleaned;
}

function loadConfig(scriptId: string, scriptSrc: string) {
  const script = document.createElement('script');
  script.id = scriptId;
  script.crossOrigin = '';
  script.src = scriptSrc;
  script.type = 'module';
  document.head.appendChild(script);
}

export function loadAppConfigs(apps: Array<{ accessRoleName?: string; slug: string; baseUrl?: string; [key: string]: unknown }>) {
  // Don't process permissions if there are no apps or we're in an error state
  if (!apps || apps.length === 0) {
    $componentApps.next([]);
    return;
  }

  appBaseUrls.clear();
  canonicalSlugs.clear();

  var cachebuster = Math.round(new Date().getTime() / 1000);
  apps.forEach(app => {
    // Sanitize slug by removing any quotes or invalid characters
    const cleanSlug = app.slug.replace(/['"]/g, '').trim();
    const slugKey = normalizeSlug(app.slug);
    const configuredBaseUrl = (app.baseUrl || process.env.REACT_APP_CDN_HOST || '').replace(/\/+$/, '');
    appBaseUrls.set(slugKey, configuredBaseUrl);
    canonicalSlugs.set(slugKey, cleanSlug);
    const scriptId = `micro-frontend-config--${cleanSlug}`;
    const scriptSrc = `${configuredBaseUrl}/apps/${cleanSlug}/static/js/amplifyConfig.js?v=${cachebuster}`;
    loadConfig(scriptId, scriptSrc);
  });

  // Check permissions after token is verified (this will be called with authenticated requests only)
  const userRoles = window.amplify?.roles || [];

  // Only mark permission revoked if user has no meaningful roles and trying to access restricted apps
  var isPermissionRevoked = false;

  const restrictedApps = apps.filter(
    x =>
      x.name !== 'Reporting' &&
      x.name !== 'DevBuilder' &&
      x.name !== 'Constituent Snapshot' &&
      x.name !== 'Role Management'
  );

  // If user has apps but no 'Amplify-User' role and trying to access restricted content
  if (restrictedApps.length > 0 && userRoles.length === 0) {
    isPermissionRevoked = true;
  }

  if (isPermissionRevoked) {
    $componentApps.next([]);
  }
}

var defaultVersions = [
  {
    version: AppDetails.version,
    commitHash: process.env.COMMITHASH,
    name: packageJson.name,
    author: 'Amplify',
    releaseDate: process.env.RELEASEDATE,
  } as Version,
];

const $amplifyApps = new BehaviorSubject<SubApp[]>([]);
const $widgetApps = new BehaviorSubject<AmplifyWidget[]>([]);
const $componentApps = new BehaviorSubject<Component[]>([]);
const $componentAppsLoaded = new BehaviorSubject<boolean>(false);
const $navigationItems = new BehaviorSubject<NavigationLink[]>([]);
const $coreNavigationItems = new BehaviorSubject<CoreNavigationLink[]>([]);
const $hasToken = new BehaviorSubject<boolean>(false);
const $versions = new BehaviorSubject<Version[]>(defaultVersions);

export let onAmplifyApps = () => $amplifyApps.asObservable() as Observable<SubApp[]>;
export let onWidgetApps = () => $widgetApps.asObservable() as Observable<AmplifyWidget[]>;
export let onNavigationItems = () => $navigationItems.asObservable() as Observable<NavigationLink[]>;
export let onComponentApps = () => $componentApps.asObservable() as Observable<Component[]>;
export let onComponentAppsLoaded = () => $componentAppsLoaded.asObservable() as Observable<boolean>;
export let onCoreNavigationItems = () => $coreNavigationItems.asObservable() as Observable<NavigationLink[]>;
export let onHasToken = () => $hasToken.asObservable() as Observable<boolean>;
export let onVersions = () => $versions.asObservable() as Observable<Version[]>;

export let urlHistory = new UrlHistory();

let amplifyToken: string;
let axiosInstance: AxiosInstance;

export function setOktaToken(newToken: string) {
  if (amplifyToken !== newToken) {
    amplifyToken = newToken;
    const defaultOptions = {
      baseURL: `${config.amplifyApiHost}/v1/`,
      headers: {
        Accept: 'text/plain',
        'Content-Type': 'application/json-patch+json',
        client_id: `${config.amplifyClientId}`,
      },
    };

    axiosInstance = axios.create(defaultOptions);
    axiosInstance.interceptors.request.use((axiosConfig) => {
      if (axiosConfig.headers) {
        axiosConfig.headers.Authorization = amplifyToken ? `Bearer ${amplifyToken}` : '';
      }
      return axiosConfig;
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    window.amplify.axios = axiosInstance as any;
    window.amplify.token = amplifyToken;

    try {
      const jwt: JwtToken = JSON.parse(atob(amplifyToken.split('.')[1]));
      window.amplify.roles = jwt.Roles;
    } catch {}

    $hasToken.next(true);
  } else if (!newToken) {
    $hasToken.next(false);
  }
}

export function registerCoreNavigationItem(app: CoreNavigationLink) {
  const coreNavigationItems = $coreNavigationItems.getValue();
  if (coreNavigationItems.length == 0 || coreNavigationItems.filter(x => x.text === app.text).length == 0) {
    coreNavigationItems.push(app);
    $coreNavigationItems.next(coreNavigationItems);
  }
}

/* this is how we register the internal apps - to be deprecated*/
export function registerSubApp(app: SubApp) {
  const amplifyApps = $amplifyApps.getValue();
  amplifyApps.push(app);
  $amplifyApps.next(amplifyApps);
}

function registerApp(module: AmplifyApp) {
  const amplifyApps = $amplifyApps.getValue();
  const cleanSlug = module.slug.replace(/['"]/g, '').trim();
  const moduleBaseUrl = (module as AmplifyApp & { baseUrl?: string }).baseUrl;

  if (moduleBaseUrl) {
    appBaseUrls.set(normalizeSlug(cleanSlug), moduleBaseUrl.replace(/\/+$/, ''));
  }

  if (amplifyApps.length == 0 || amplifyApps.filter(x => x.slug === module.slug).length == 0) {

    // Load CSS for the registered app
    if (window.amplify?.loadCss) {
      window.amplify.loadCss({ slug: cleanSlug, moduleName: cleanSlug });
    }

    amplifyApps.push(InitSubApp(module));
    $amplifyApps.next(amplifyApps);
  }
}

function registerWidget(widget: AmplifyWidget) {
  const widgetApps = $widgetApps.getValue();
  widgetApps.push(widget);
  $widgetApps.next(widgetApps);
}

export function registerComponentApp(app: Component) {
  const componentApps = $componentApps.getValue();

  // Load CSS for the registered component app
  if (window.amplify?.loadCss) {
    window.amplify.loadCss({ slug: app.slug, moduleName: app.slug });
  }

  componentApps.push(app);
  $componentApps.next(componentApps);
  let componentLoad = $componentAppsLoaded.getValue();
  $componentAppsLoaded.next(!componentLoad);
}

export function registerNavigationItem(app: NavigationLink) {
  const navigationItems = $navigationItems.getValue();
  if (navigationItems.length == 0 || navigationItems.filter(x => x.text === app.text).length == 0) {
    navigationItems.push(app);
    $navigationItems.next(navigationItems);
  }
}

export function registerVersion(version: Version) {
  const versions = $versions.getValue();
  versions.push(version);
  $versions.next(versions);
}

function loadCss(cssInfo: CSSInfo) {
  const { slug, moduleName } = cssInfo;

  // Sanitize slug and moduleName by removing any quotes or invalid characters
  const cleanSlug = slug.replace(/['"]/g, '').trim();
  const cleanModuleName = moduleName.replace(/['"]/g, '').trim();

  const cssid = `micro-frontend-css--${cleanSlug}`;
  if (document.getElementById(cssid)) {
    return;
  }

  const baseUrl = getAppBaseUrl(cleanSlug);
  const urlSlug = getAppSlug(cleanSlug);
  const host = `${baseUrl}/apps/${urlSlug}`;
  var cachebuster = Math.round(new Date().getTime() / 1000);

  // Special cases: component-navigation and dev-builder use their own CSS filenames, others use microApp.css
  const cssFileName = (normalizeSlug(cleanSlug) === 'component-navigation') ? cleanModuleName : 'microApp';

  const style = document.createElement('link');
  style.id = cssid;
  style.rel = 'stylesheet';
  style.href = `${host}/static/css/${cssFileName}.css?v=${cachebuster}`;
  document.head.appendChild(style);
}
window.amplify = {
  ...(window.amplify ?? {}),
  registerWidget,
  registerApp,
  registerComponentApp,
  registerNavigationItem,
  registerCoreNavigationItem,
  registerVersion,
  onAmplifyApps,
  onWidgetApps,
  onNavigationItems,
  onCoreNavigationItems,
  onHasToken,
  onVersions,
  urlHistory,
  loadCss,
} as typeof window.amplify;

// Augment window.amplify with the baseUrl resolver so sub-apps / SDK can share one source of truth.
(window.amplify as typeof window.amplify & { getAppBaseUrl: (slug: string) => string; getAppSlug: (slug: string) => string }).getAppBaseUrl = getAppBaseUrl;
(window.amplify as typeof window.amplify & { getAppBaseUrl: (slug: string) => string; getAppSlug: (slug: string) => string }).getAppSlug = getAppSlug;

// Ensure this file is treated as a module
export {};
